import {
  DescriptionIcon,
  ForumIcon,
  HamburgerIcon,
  HelpIcon,
  NotificationIcon,
  SearchIcon,
  SettingsIcon,
  ShoutIcon,
} from '../../assets/icons/headerIcons';
import './Header.css';
import Attendance from '@components/Attendance';
import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import type { AlertNotification, AlertSideMenu, BetaInfo } from '../../types/dashboard';
import { NotificationList } from '../NotificationList/NotificationList';
import { SettingsMenu } from './SettingsMenu';
import SearchAutocomplete from '../TopSearchResult/SearchAutocomplete';
import { TopSearchResult } from '../TopSearchResult';
import type { SearchResultItem } from '@services/searchService';
import corgeeLogo from '../../assets/logos/logo-corgee.png';
import { config, js } from '@config/env';
import { Dialog, DialogContent } from '../ui/Dialog';
import { X } from 'lucide-react';
import { useInfoWindow } from '../../contexts/InfoWindowContext';
import { useAlertNotifications } from '../../contexts/AlertNotificationsContext';
import NewTicket from '../Globals/NewTicket';
import { authStorage, sessionStorageUtil, cn } from '@/lib';
import { apiService } from '@services/api';
import { useAuth } from '@contexts/AuthContext';
import { useSocketConnectionId } from '@contexts/SocketContext';
import { getSideMenuLocal, extractPersonIdFromNotification } from '../utils/CorgeeHelper';
import { buildChatWindowUrl, getChatBaseUrl } from '@lib/chatUrl';

interface HeaderProps {
  user: {
    name: string;
    email?: string;
  };
  onMenuToggle?: () => void;
  unreadMessages?: number;
  betaData: BetaInfo | null;
  brandImageVersion?: number;
  alertSideMenu: AlertSideMenu[];
  onRefreshAlerts?: () => void;
  onDecrementAlertCount?: (configValueId: number, amount?: number) => void;
}

const openChat = () =>
{
  const urlChat = getChatBaseUrl() + '/';

  if (window.chatWebReact != null)
  {
    try
    {
      if (
        window.chatWebReact.location != null &&
        window.chatWebReact.location.origin + window.chatWebReact.location.pathname === urlChat &&
        !window.chatWebReact.closed
      )
      {
        if (navigator.userAgent.includes('Firefox'))
        {
          window.chatWebReact.close();
          window.chatWebReact = window.open(urlChat, 'chatWebReact') ?? null;
        }
        window.chatWebReact?.focus();
      }
      else
      {
        window.chatWebReact = window.open(urlChat, 'chatWebReact') ?? null;
      }
    }
    catch
    {
      window.chatWebReact = window.open(urlChat, 'chatWebReact') ?? null;
    }
  }
  else
  {
    window.chatWebReact = window.open(urlChat, 'chatWebReact') ?? null;
    window.chatWebReact?.focus();
  }
};

// Opens (or creates) a private chat with the given person and focuses the chat window
const openChatWithPerson = async (personId: number) =>
{
  if (!personId) return;

  const currentPersonId = parseInt(authStorage.getPersonId() || '0');
  if (!currentPersonId) return;

  try
  {
    const response = await apiService.post<{ ChatID?: number }>(
      'Person/CreateNewPrivateChat',
      {
        Participant1: currentPersonId,
        Participant2: personId,
        ChatID: 0,
      },
    );

    if (!response?.ChatID) return;

    const chatPersonUrl = buildChatWindowUrl(response.ChatID);
    window.chatWebReact = window.open(chatPersonUrl, 'chatWebReact') ?? null;
    window.chatWebReact?.focus();
  }
  catch (error)
  {
    console.error('Error opening chat with person:', error);
  }
};

const openWikiLink = () =>
{
  const urlWiki = config.urls.wiki;
  if (urlWiki)
  {
    window.open(urlWiki, 'wikiLink');
  }
};

// openSettings is now managed via state inside the component

export const Header: React.FC<HeaderProps> = ({
  user: _user,
  onMenuToggle,
  unreadMessages = 0,
  betaData,
  brandImageVersion,
  alertSideMenu,
  onRefreshAlerts,
  onDecrementAlertCount,
}) =>
{
  void _user;
  const socketConnectionId = useSocketConnectionId();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [releaseNotesUrl, setReleaseNotesUrl] = useState(config.urls.documentation);
  const [searchText, setSearchText] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showTopSearchResult, setShowTopSearchResult] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const topSearchResultRef = useRef<InstanceType<typeof TopSearchResult>>(null);
  const navigate = useNavigate();

  const BrandName: string = betaData
    ? betaData.isEnabledCustomBranding
      ? betaData.brandName
      : 'Corgee'
    : 'Corgee';
  const rawBrandImage: string = betaData
    ? betaData.isEnabledCustomBranding
      ? betaData.brandImage
      : ''
    : '';
  const resolvedBrandImage: string = rawBrandImage
    ? rawBrandImage.startsWith('http://') || rawBrandImage.startsWith('https://')
      ? rawBrandImage
      : `${String(js.settings.apiAuthURL || '').replace(/\/?$/, '/')}${rawBrandImage.replace(/^\//, '')}`
    : '';
  // Append a version token so a replaced image under the same path bypasses the browser cache
  const BrandImage: string = resolvedBrandImage
    ? brandImageVersion
      ? `${resolvedBrandImage}${resolvedBrandImage.includes('?') ? '&' : '?'}v=${brandImageVersion}`
      : resolvedBrandImage
    : corgeeLogo;

  // Enable marquee only when the name really overflows its container
  const brandLinkRef = useRef<HTMLAnchorElement>(null);
  const brandTextRef = useRef<HTMLSpanElement>(null);
  const [isBrandOverflowing, setIsBrandOverflowing] = useState(false);
  const [marqueeDuration, setMarqueeDuration] = useState(0);

  useLayoutEffect(() =>
  {
    const link = brandLinkRef.current;
    const text = brandTextRef.current;
    if (!link || !text)
    {
      setIsBrandOverflowing(false);
      return;
    }
    const overflowPx = text.scrollWidth - link.clientWidth;
    setIsBrandOverflowing(overflowPx > 0);
    // Scale duration to the hidden distance (~40px/s per scroll leg, which is 45% of the cycle)
    setMarqueeDuration(Math.max(2, overflowPx / 40 / 0.45));
  }, [BrandName, betaData?.shape]);

  const totalNotifications: number = alertSideMenu.reduce((acc, item) =>
  {
    return acc + item.Total;
  }, 0);
  const hasWarning: boolean = alertSideMenu.some(item => item.HasWarning);

  // New Ticket
  const tenantData = authStorage.getTenantData();
  const { user: UserData } = useAuth();
  let SupportConfigValue: any = {};
  const sideMenu = getSideMenuLocal();
  if (sideMenu)
  {
    const item = sideMenu.find(function (item)
    {
      return item.ConfigValueID == config.support.ticketConfigValueId;
    });
    if (item && item.ConfigValueID)
    {
      SupportConfigValue = item;
    }
  }
  if (!SupportConfigValue.ConfigValueId)
  {
    SupportConfigValue = {
      ConfigValueId: config.support.ticketConfigValueId,
      Text: 'Ticket',
      Icon: 'icon2-Tickets',
    };
  }

  const personLayers = sessionStorageUtil.getAllLayers();
  let SupportLayer: any = {};

  if (personLayers)
  {
    const layer = personLayers.find(function (item)
    {
      return item.LayerId == config.support.layerId;
    });
    if (layer && layer.LayerId)
    {
      SupportLayer = layer;
    }
  }

  if (!SupportLayer.LayerId)
  {
    SupportLayer = {
      Color: '#7cc899',
      LayerId: config.support.layerId,
      LayerName: 'Support',
    };
  }

  const Conversation = {
    Attachments: [],
    Body: '',
    ConfigValueId: config.support.ticketConfigValueId,
    ConversationId: 0,
    ConversationLayers: [
      {
        LayerId: config.support.layerId,
      },
    ],
    CreatedById: 1,
    IsFavorite: false,
    IsRedFlag: false,
    Metadata: {
      TenantId: config.support.mainTenantId,
      PersonId:
        config.support.mainTenantId == tenantData?.Id
          ? UserData?.personid
          : config.support.personIdToPost,
      UserEmailAddress: UserData?.username,
      CurrentUserName: UserData?.givenname,
      SupportEmail: config.support.email,
      TicketConfigValueId: config.support.ticketConfigValueId,
      SupportProjectId: config.support.projectId,
      TenantName: tenantData?.TenancyName,
    },
    ProjectId: config.support.projectId,
    ProjectName: 'Corgee_Support',
    TenantId: config.support.mainTenantId,
    Title: '',
    UpdatedBy: 1,
    SocketConnectionId: socketConnectionId,
  };

  const { openInfoWindow } = useInfoWindow();
  const { markConversationRead } = useAlertNotifications();

  const openNotifications = () =>
  {
    setShowNotifications(prev => !prev);
  };

  const openNewTicket = () =>
  {
    setShowNewTicket(true);
  };

  const handleSelectNotification = (notification: AlertNotification, fromSummary?: boolean) =>
  {
    if (notification.ConversationId)
    {
      markConversationRead(notification.ConversationId);
    }
    if (notification.Type === 'Users')
    {
      const personId = extractPersonIdFromNotification(notification.Body);
      if (personId)
      {
        navigate(`/Person/Authorize/${personId}`);
        return;
      }
    }
    const isPTONotification =
      notification.Type === 'PTO Report - Request Approved' ||
      notification.Type === 'PTO Report - Request Rejected';
    if (isPTONotification)
    {
      if (notification.Body)
      {
        const parser = new DOMParser();
        const doc = parser.parseFromString(notification.Body, 'text/html');
        const linkEl = doc.querySelector('#labela') as HTMLAnchorElement | null;
        const href = linkEl?.getAttribute('href') ?? '';
        const hashIndex = href.indexOf('#/');
        if (hashIndex !== -1)
        {
          navigate(href.slice(hashIndex + 1));
          return;
        }
      }
      if (notification.ConversationId)
      {
        navigate(`/PTOReport/${notification.ConversationId}`);
        return;
      }
    }
    if (notification.ConversationId)
    {
      openInfoWindow(
        notification.ConversationId,
        undefined,
        undefined,
        fromSummary ? { toggle: false } : undefined,
      );
      return;
    }
    // For notifications without a ConversationId, extract the route from the body link
    if (notification.Body)
    {
      const parser = new DOMParser();
      const doc = parser.parseFromString(notification.Body, 'text/html');
      const linkEl = doc.querySelector('#labela') as HTMLAnchorElement | null;
      const href = linkEl?.getAttribute('href') ?? '';
      const hashIndex = href.indexOf('#/');
      if (hashIndex !== -1)
      {
        const route = href.slice(hashIndex + 1); // e.g. "/VacationsApproval"
        navigate(route);
      }
    }
  };

  const handleSearchFocus = () =>
  {
    if (showTopSearchResult) return;
    setShowAutocomplete(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const value = e.target.value;
    setSearchText(value);
    setShowAutocomplete(true);
    setShowTopSearchResult(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter' && searchText.trim().length >= 3)
    {
      e.preventDefault();
      setShowAutocomplete(false);
      setShowTopSearchResult(true);

      // Show TopSearchResult via its show() method
      setTimeout(() =>
      {
        if (topSearchResultRef.current && searchInputRef.current)
        {
          topSearchResultRef.current.show(searchInputRef.current, { ConfigValueId: 0 });
        }
      }, 0);
    }
  };

  const handleCloseAutocomplete = () =>
  {
    setShowAutocomplete(false);
  };

  const getSearchInputRect = (): DOMRect =>
  {
    if (searchInputRef.current)
    {
      return searchInputRef.current.getBoundingClientRect();
    }
    return new DOMRect(0, 39, 300, 30);
  };

  const handleSelectProject = useCallback(
    (item: SearchResultItem) =>
    {
      navigate(`/ProjectDetails/${item.ID}?ProjectName=${encodeURIComponent(item.Name)}`);
    },
    [navigate],
  );

  const handleSelectDashboard = useCallback(
    (item: SearchResultItem) =>
    {
      if (item.ProjectId && item.ProjectId > 0)
      {
        navigate(`/ProjectDetails/${item.ProjectId}?ProjectDashboardId=${item.ID}`);
      }
      else
      {
        navigate(`/Dashboards/${item.ID}`);
      }
    },
    [navigate],
  );

  const handleSelectPerson = useCallback((item: SearchResultItem) =>
  {
    void openChatWithPerson(item.ID);
  }, []);

  const handleSelectItem = useCallback(
    (item: SearchResultItem) =>
    {
      openInfoWindow(item.ID);
    },
    [openInfoWindow],
  );

  const getNotificationPosition = () =>
  {
    if (bellRef.current)
    {
      const rect = bellRef.current.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.bottom + 4,
        bellCenterX: rect.left + rect.width / 2,
        bellCenterY: rect.top + rect.height / 2,
      };
    }
    return { left: 0, top: 39, bellCenterX: 16, bellCenterY: 20 };
  };

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <div className="dsh-header">
      {/* Left Section */}
      <div className="dsh-header__left">
        <button
          onClick={onMenuToggle}
          className="dsh-header__menu-btn"
          title="Resize Navigation Bar"
        >
          <HamburgerIcon className="dsh-header__hamburger" />
        </button>

        <div className="dsh-header__brand">
          {/* Logo Icon */}
          <div className={cn(
            'dsh-header__logo-bg',
            betaData?.shape === 'square' && 'dsh-header__logo-bg--square',
            betaData?.shape === 'rectangle' && 'dsh-header__logo-bg--rectangle',
          )}>
            <img
              className={cn(
                'dsh-header__logo-img',
                betaData?.shape === 'square' && 'dsh-header__logo-img--square',
                betaData?.shape === 'rectangle' && 'dsh-header__logo-img--rectangle',
              )}
              src={BrandImage}
            />
          </div>
          {/* Title — hidden for rectangle shape (logo fills the space) */}
          {betaData?.shape !== 'rectangle' && (
            <Link
              ref={brandLinkRef}
              to="/home"
              className="dsh-header__brand-name"
              title={BrandName}
            >
              <span
                ref={brandTextRef}
                style={
                  isBrandOverflowing
                    ? ({ '--marquee-duration': `${marqueeDuration}s` } as React.CSSProperties)
                    : undefined
                }
                className={cn(
                  isBrandOverflowing ? 'dsh-header__brand-text' : 'dsh-header__brand-text_default',
                )}
              >
                {BrandName}
              </span>
            </Link>
          )}
        </div>
      </div>

      {/* Center Section - Search */}
      <div className="dsh-header__center">
        {/* Notification Bell */}
        <button
          ref={bellRef}
          onClick={openNotifications}
          className={`dsh-header__bell-btn${hasWarning ? ' dsh-header__bell-btn--warning' : ''}`}
          title="Notifications"
        >
          {hasWarning ? (
            <span
              className="material-icons"
              style={{ fontSize: '16px', color: isDark ? '#b1a99d' : '#999994' }}
            >
              warning
            </span>
          ) : (
            <NotificationIcon className="dsh-header__btn-icon" />
          )}
          {totalNotifications > 0 && (
            <span className="dsh-header__notif-badge">
              {totalNotifications > 99 ? '99+' : totalNotifications}
            </span>
          )}
        </button>

        {showNotifications && (
          <NotificationList
            configValueId={0}
            position={getNotificationPosition()}
            onClose={() => setShowNotifications(false)}
            onSelectNotification={handleSelectNotification}
            onRefreshAlerts={onRefreshAlerts}
            onDecrementAlertCount={onDecrementAlertCount}
            showArrow={false}
          />
        )}

        <div className="dsh-header__search-wrap">
          <SearchIcon className="dsh-header__search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchText}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search dashboards, people and projects..."
            className="dsh-header__search-input"
          />
          {showAutocomplete && (
            <SearchAutocomplete
              searchText={searchText}
              anchorRect={getSearchInputRect()}
              onClose={handleCloseAutocomplete}
              onSelectProject={handleSelectProject}
              onSelectDashboard={handleSelectDashboard}
              onSelectPerson={handleSelectPerson}
              onSelectItem={handleSelectItem}
            />
          )}
        </div>
      </div>

      {/* Right Section */}
      <div className="dsh-header__right">
        {/* Action Buttons */}
        <Attendance />

        <button
          onClick={openChat}
          className={`dsh-header__chat-btn${unreadMessages > 0 ? ' dsh-header__chat-btn--unread' : ''}`}
          title="Chat"
        >
          <ForumIcon className="dsh-header__btn-icon" />
          {unreadMessages > 0 && <span className="dsh-header__chat-badge">{unreadMessages}</span>}
        </button>

        <button onClick={openWikiLink} className="dsh-header__icon-btn" title="Wiki Links">
          <DescriptionIcon className="dsh-header__btn-icon" />
        </button>

        <button
          onClick={() =>
          {
            setReleaseNotesUrl(config.urls.documentation);
            setShowReleaseNotes(true);
          }}
          className="dsh-header__icon-btn"
          title="Release Notes"
        >
          <HelpIcon className="dsh-header__btn-icon" />
        </button>

        <button
          onClick={openNewTicket}
          className="dsh-header__icon-btn"
          title="Request Support: Issues, wishlist."
        >
          <ShoutIcon className="dsh-header__btn-icon" />
        </button>

        <button
          onClick={() => setShowSettings(prev => !prev)}
          className="dsh-header__icon-btn"
          title="Settings"
        >
          <SettingsIcon className="dsh-header__btn-icon" />
        </button>

        {showSettings && (
          <SettingsMenu
            onClose={() => setShowSettings(false)}
            onOpenHelp={() =>
            {
              setReleaseNotesUrl(config.urls.documentation);
              setShowReleaseNotes(true);
            }}
          />
        )}
      </div>

      {showTopSearchResult && (
        <TopSearchResult
          ref={topSearchResultRef}
          request={{
            api: {
              url: config.api.baseUrl,
              token: authStorage.getAccessToken() || '',
              PersonId: authStorage.getPersonId() || '',
            },
            config: { weburl: config.web.url, apiurl: config.api.baseUrl },
            functions: {
              onHidePopover: () => { setShowTopSearchResult(false); },
              onClearSearch: () => { setSearchText(''); setShowAutocomplete(false); },
            },
          }}
          word={searchText}
          corgeeFunctions={{
            openInfoWindow: (conv: any) =>
            {
              const id = typeof conv === 'number' ? conv : conv?.ConversationId;
              if (id) openInfoWindow(id);
            },
          }}
        />
      )}

      {showNewTicket && (
        <NewTicket
          open={showNewTicket}
          setOpen={() => setShowNewTicket(false)}
          config={
            {
              params: {
                SupportLayer: SupportLayer,
                SupportConfigValue: SupportConfigValue,
                SupportProjectId: config.support.projectId,
                Conversation: Conversation,
              },
              apiUrl: config.api.authUrl || 'http://localhost/Corgee.Web/',
              authToken: UserData?.access_token || '',
            } as any
          }
        />
      )}

      {/* Release Notes / Help & Learning Dialog */}
      <Dialog open={showReleaseNotes} onOpenChange={setShowReleaseNotes}>
        <DialogContent className="max-w-[870px] p-0 dark:bg-[#333535] dark:border-[#4a4c4c]">
          {/* Close Button */}
          <div className="flex items-center justify-end min-h-[40px] px-2 dark:bg-[#333535]">
            {releaseNotesUrl !== config.urls.documentation && (
              <button
                onClick={() => setReleaseNotesUrl(config.urls.documentation)}
                className="mr-auto text-[#62accb] text-xs cursor-pointer hover:opacity-80 flex items-center"
              >
                <span className="material-icons text-sm pr-2">arrow_back</span>
                Back to Help & Learning
              </button>
            )}
            <button
              onClick={() => setShowReleaseNotes(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-[#6e6860] dark:hover:text-[#a9a093]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Release Notes Ticker Banner */}
          {config.urls.releaseNotes && releaseNotesUrl === config.urls.documentation && (
            <div className="bg-[#79c5dd] dark:bg-[#3c6c80] text-white text-[11px] font-light ml-4 mr-4 flex items-center justify-center py-2">
              <div className="flex-shrink-0 pr-2">
                <img
                  className="w-[80px] h-[80px] -mt-2 -mb-2"
                  src="data:image/png;base64,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"
                  alt=""
                />
              </div>
              <div className="px-4">
                <span className="text-base font-normal">Check our latest release notes!</span>
              </div>
              <div className="flex-shrink-0 pl-4 pr-4">
                <button
                  onClick={() => setReleaseNotesUrl(config.urls.releaseNotes)}
                  className="bg-white text-[#62accb] px-2 py-1 font-normal text-xs border border-white cursor-pointer hover:opacity-80"
                >
                  Read More
                </button>
              </div>
            </div>
          )}

          {/* Iframe Container */}
          <div className="ml-4 mr-4 mt-2 mb-6 dark:bg-[#333535]">
            <iframe
              src={releaseNotesUrl}
              className="w-full h-[440px] border border-[#ddd] dark:border-[#4a4c4c] block"
              frameBorder="0"
              title="Help & Learning"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
